import { getContext, setContext } from "svelte";

const DATE_PICKER_CONTEXT = Symbol("date-picker-context");

class DatePickerContext {
  #getSelectedDate = null;
  #setSelectedDate = null;
  #pendingDate = $state(null);
  #initialDate = $state(null);
  #showModal = $state(false);
  #allowFuture = $state(true);
  #allowPast = $state(true);
  #includeTime = $state(false);
  #selectedTime = $state(null);
  #customButtonText = null;
  #modalTitle = null;
  #onApplyCallback = null;
  #onCancelCallback = null;

  constructor(options = {}) {
    const {
      getSelectedDate,
      setSelectedDate,
      initialDate = null,
      allowFuture = true,
      allowPast = true,
      includeTime = false,
      showModal = false,
      customButtonText = null,
      modalTitle = null,
      onApply = null,
      onCancel = null,
    } = options;

    this.#getSelectedDate = getSelectedDate;
    this.#setSelectedDate = setSelectedDate;
    this.#initialDate = initialDate;
    this.#allowFuture = allowFuture;
    this.#allowPast = allowPast;
    this.#includeTime = includeTime;
    this.#showModal = showModal;
    this.#customButtonText = customButtonText;
    this.#modalTitle = modalTitle;
    this.#onApplyCallback = onApply;
    this.#onCancelCallback = onCancel;

    if (includeTime) {
      this.#selectedTime = initialDate
        ? this.#extractTime(initialDate)
        : this.#getCurrentTime();
    }
  }

  get selectedDate() {
    return this.#getSelectedDate();
  }

  get pendingDate() {
    return this.#pendingDate;
  }

  get initialDate() {
    return this.#initialDate;
  }

  get showModal() {
    return this.#showModal;
  }

  get allowFuture() {
    return this.#allowFuture;
  }

  get allowPast() {
    return this.#allowPast;
  }

  get includeTime() {
    return this.#includeTime;
  }

  get selectedTime() {
    return this.#selectedTime;
  }

  get customButtonText() {
    return this.#customButtonText;
  }

  get modalTitle() {
    return this.#modalTitle;
  }

  get onApply() {
    return this.#onApplyCallback;
  }

  get onCancel() {
    return this.#onCancelCallback;
  }

  set selectedDate(date) {
    this.#setSelectedDate(date);
  }

  set pendingDate(date) {
    this.#pendingDate = date;
  }

  set selectedTime(time) {
    this.#selectedTime = time;
  }

  set showModal(show) {
    this.#showModal = show;
    if (!show) {
      this.#pendingDate = null;
    }
  }

  reset() {
    this.#setSelectedDate(this.#initialDate);
    this.#pendingDate = null;
    this.#showModal = false;
  }

  applyPendingDate() {
    if (this.#pendingDate) {
      let finalDate = this.#pendingDate;

      if (this.#includeTime && this.#selectedTime) {
        finalDate = this.combineDateTime(this.#pendingDate, this.#selectedTime);
      }

      this.#setSelectedDate(finalDate);
      this.#pendingDate = null;
      this.#showModal = false;

      if (this.#onApplyCallback) {
        this.#onApplyCallback(finalDate);
      }

      return true;
    }
    return false;
  }

  setPendingDate(date) {
    this.#pendingDate = date;
  }

  isDateAllowed(date) {
    if (!date) return false;

    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const checkDate = new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
    );

    if (!this.#allowFuture && checkDate > today) {
      return false;
    }

    if (!this.#allowPast && checkDate < today) {
      return false;
    }

    return true;
  }

  formatDate(date, locale = "en-GB") {
    if (!date) return null;

    const month = date.toLocaleString(locale, { month: "short" });
    const day = date.getDate();
    const year = date.getFullYear();

    return `${month} ${day} ${year}`;
  }

  #extractTime(date) {
    if (!date) return null;
    if (!(date instanceof Date)) return null;

    const hours = date.getHours().toString().padStart(2, "0");
    const minutes = date.getMinutes().toString().padStart(2, "0");
    return `${hours}:${minutes}`;
  }

  #getCurrentTime() {
    const now = new Date();
    const hours = now.getHours().toString().padStart(2, "0");
    const minutes = now.getMinutes().toString().padStart(2, "0");
    return `${hours}:${minutes}`;
  }

  combineDateTime(date, timeString) {
    if (!date) return null;

    // Ensure date is a valid Date object
    const dateObj = date instanceof Date ? date : new Date(date);
    if (isNaN(dateObj.getTime())) return null;

    const resultDate = new Date(dateObj);

    if (timeString) {
      const match = timeString.match(/^(\d{1,2}):(\d{2})$/);
      if (match) {
        const hours = parseInt(match[1], 10);
        const minutes = parseInt(match[2], 10);
        resultDate.setHours(hours, minutes, 0, 0);
      }
    }

    return resultDate;
  }
}

export const createDatePickerContext = (options) => {
  const context = new DatePickerContext(options);
  setContext(DATE_PICKER_CONTEXT, context);
  return context;
};

export const getDatePickerContext = () => getContext(DATE_PICKER_CONTEXT);
